import moment from "moment";
import { IContractCruMetric, IContractCruSide } from "dto/Contracts/IContractCru";

// Walidacja metryki CRU po stronie klienta — lustro BE ContractCruValidator
// (port reguł 4 kroków kreatora ContractCruMetricViewModel z Magistratu).
// BE waliduje ponownie (plus: własna jednostka nie może być stroną), tu chodzi o
// czytelne komunikaty przy każdym kroku bez odpytywania serwera.

const MIN_PERIOD = moment("2026-01-01");
const MAX_PERIOD = moment("2500-12-31");
const POSTAL_CODE = /^\d{2}-\d{3}$/;

const blank = (s?: string | null) => !s || !s.trim();
const isPoland = (c?: string | null) => (c ?? "").trim().toUpperCase() === "PL";

export const hasAnyAddress = (s: IContractCruSide) =>
  !blank(s.Street) ||
  !blank(s.BuildingNumber) ||
  !blank(s.City) ||
  !blank(s.PostalCode) ||
  !blank(s.Voivodeship) ||
  !blank(s.County) ||
  !blank(s.Municipality);

const isCompletePolishAddress = (s: IContractCruSide) =>
  !blank(s.City) &&
  !blank(s.PostalCode) &&
  POSTAL_CODE.test(s.PostalCode!.trim()) &&
  !blank(s.Voivodeship) &&
  !blank(s.County) &&
  !blank(s.Municipality);

/** Czy okres jest podawany datami od–do (a nie liczbą + jednostką). */
export const periodByDates = (m: IContractCruMetric) =>
  !!m.PeriodFrom || !!m.PeriodTo || !(m.PeriodCount || m.PeriodUnit);

export const validateStep1 = (m: IContractCruMetric): string[] => {
  const e: string[] = [];
  if (!m.AgreementDate) e.push("Podaj datę zawarcia umowy.");
  if (!m.HasNoAgreementNumber && blank(m.AgreementNumber))
    e.push("Podaj numer umowy lub zaznacz „Brak numeru umowy”.");

  if (m.IndefinitePeriod) return e;

  if (!periodByDates(m)) {
    if (!m.PeriodCount || m.PeriodCount <= 0)
      e.push("Podaj liczbę okresu (większą od zera).");
    if (!m.PeriodUnit) e.push("Wybierz jednostkę okresu.");
    return e;
  }

  const from = m.PeriodFrom ? moment(m.PeriodFrom).startOf("day") : null;
  const to = m.PeriodTo ? moment(m.PeriodTo).startOf("day") : null;
  if (!from) e.push("Podaj datę „Okres od”.");
  if (!to) e.push("Podaj datę „Okres do”.");
  if (from && from.isBefore(MIN_PERIOD))
    e.push("„Okres od” nie może być wcześniejszy niż 01.01.2026.");
  if (from && from.isAfter(moment().add(1, "year")))
    e.push("„Okres od” nie może być późniejszy niż rok od dnia dzisiejszego.");
  if (to && to.isBefore(MIN_PERIOD))
    e.push("„Okres do” nie może być wcześniejszy niż 01.01.2026.");
  if (to && to.isAfter(MAX_PERIOD))
    e.push("„Okres do” nie może być późniejszy niż 31.12.2500.");
  if (from && to && to.isBefore(from))
    e.push("„Okres do” nie może być wcześniejszy niż „Okres od”.");
  return e;
};

export const validateStep2 = (m: IContractCruMetric): string[] => {
  const e: string[] = [];
  if (!m.Sides?.length) {
    e.push("Dodaj co najmniej jedną stronę umowy.");
    return e;
  }
  m.Sides.forEach((s, i) => {
    const p = `Strona ${i + 1}`;
    if (blank(s.Country)) e.push(`${p}: podaj kraj.`);

    if (s.Confidentiality) {
      if (blank(s.Confidentiality.Authority))
        e.push(`${p}: podaj organ/osobę wyłączającą jawność.`);
      if (s.Confidentiality.Basis === "INNA" && blank(s.Confidentiality.Comment))
        e.push(`${p}: komentarz jest wymagany dla podstawy „Inna”.`);
    } else if (s.SideType === "SU02") {
      if (blank(s.FirstName)) e.push(`${p}: podaj imię.`);
      if (blank(s.LastName)) e.push(`${p}: podaj nazwisko.`);
    } else if (isPoland(s.Country)) {
      if (blank(s.Nip) && blank(s.Regon))
        e.push(`${p}: dla strony z Polski podaj NIP lub REGON.`);
      if (s.SideType === "SU01" && hasAnyAddress(s) && !isCompletePolishAddress(s))
        e.push(
          `${p}: dane adresowe są niekompletne (wymagane: miejscowość, kod pocztowy XX-XXX, województwo, powiat, gmina/miasto/dzielnica).`,
        );
    } else {
      if (s.SideType === "SU03")
        e.push(`${p}: JSFP jest dozwolone wyłącznie dla kraju PL.`);
      if (blank(s.Name)) e.push(`${p}: podaj nazwę.`);
      if (blank(s.BuildingNumber)) e.push(`${p}: podaj numer nieruchomości.`);
      if (blank(s.PostalCode)) e.push(`${p}: podaj kod pocztowy.`);
      if (blank(s.City)) e.push(`${p}: podaj miejscowość.`);
    }
  });
  return e;
};

export const validateStep3 = (m: IContractCruMetric): string[] => {
  const e: string[] = [];
  if (m.SubjectConfidentiality) {
    if (blank(m.SubjectConfidentiality.Authority))
      e.push("Przedmiot umowy: podaj organ/osobę wyłączającą jawność.");
    if (
      m.SubjectConfidentiality.Basis === "INNA" &&
      blank(m.SubjectConfidentiality.Comment)
    )
      e.push("Przedmiot umowy: komentarz jest wymagany dla podstawy „Inna”.");
  } else if (blank(m.Subject)) {
    e.push("Podaj przedmiot umowy.");
  }

  if (m.ValueConfidentiality) {
    if (blank(m.ValueConfidentiality.Authority))
      e.push("Wartość umowy: podaj organ/osobę wyłączającą jawność.");
    if (
      m.ValueConfidentiality.Basis === "INNA" &&
      blank(m.ValueConfidentiality.Comment)
    )
      e.push("Wartość umowy: komentarz jest wymagany dla podstawy „Inna”.");
  } else if (m.Value == null || Number.isNaN(m.Value)) {
    e.push("Podaj wartość umowy.");
  } else if (m.Value < 0) {
    e.push("Wartość umowy nie może być ujemna.");
  }

  if (m.FinancedFromOtherFunds == null)
    e.push(
      "Wskaż, czy umowa jest finansowana ze środków, o których mowa w art. 5 ust. 1 pkt 2-3 ustawy o finansach publicznych.",
    );
  return e;
};

export const validateStep4 = (m: IContractCruMetric): string[] => {
  const e: string[] = [];
  if (!m.AgreementStatus) e.push("Wybierz status umowy.");
  if (m.AgreementStatus === "Nieaktywna" && !m.Changes?.length)
    e.push("Dla umowy nieaktywnej dodaj co najmniej jedną zmianę umowy.");
  (m.Changes ?? []).forEach((c, i) => {
    const p = `Zmiana ${i + 1}`;
    if (!c.ChangeDate) e.push(`${p}: podaj datę zmiany.`);
    if (blank(c.Comment)) e.push(`${p}: podaj komentarz.`);
  });
  return e;
};

export const validateCruMetric = (m: IContractCruMetric): string[][] => [
  validateStep1(m),
  validateStep2(m),
  validateStep3(m),
  validateStep4(m),
];
